/**
 * PresenceRail — "Who's around", the home page's social band: everyone seen on
 * the platform since yesterday, with their avatar, handle, and what they're
 * doing right now (`In game: <title>` / online / last seen …).
 *
 * This is the seam the wider social surface grows onto (invites, friends,
 * likes), so it is a standalone section rather than something folded into the
 * gallery grid.
 *
 * Two clocks meet here and both matter:
 *  - the SUBSCRIPTION delivers a row whenever someone beats (realtime), and
 *  - a local TICK re-derives every status on the viewer's own clock, because a
 *    row that merely goes STALE emits no event. Without the tick, someone who
 *    closes their tab stays "online" on your screen until they come back.
 *
 * The pure model ({@link visiblePresence}) owns who is listed and in what
 * order; this component only renders it. Logged-out visitors see the rail too
 * — showing them the platform is alive is the point of putting it here.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  visiblePresence,
  formatPresenceStatus,
  PRESENCE_ONLINE_MS,
  type PresenceEntry,
  type UserPresence,
} from '@djgg/creations';
import { useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { useNow } from '../hooks/useNow.js';
import { useChatDock } from '../chat/chatDockContext.js';
import { useCanChat } from '../chat/useCanChat.js';
import { UserAvatar } from './UserAvatar.js';
import { Mascot } from './Mascot.js';

/** How often statuses are re-derived against the viewer's clock. Half the
 *  online window, so a lapsed beat shows as such within one window at worst. */
const TICK_MS = PRESENCE_ONLINE_MS / 2;

/** One person's line. `in-game` links to the game so the rail is a way IN. */
function PresenceRow({ entry, onGreet }: { entry: PresenceEntry; onGreet?: (handle: string) => void }) {
  const { row, status } = entry;
  const live = status.kind === 'in-game' || status.kind === 'online';
  const label = formatPresenceStatus(status);
  return (
    <li className="presence__item" data-testid="presence-item" data-status={status.kind}>
      <span className={`presence__dot presence__dot--${live ? 'live' : 'away'}`} aria-hidden="true" />
      <UserAvatar avatarKey={row.avatarKey} size={30} alt={`@${row.handle}`} />
      <span className="presence__who">
        <Link className="presence__handle" to={`/u/${row.handle}`}>
          @{row.handle}
        </Link>
        <span className={`presence__status ${live ? 'presence__status--live' : ''}`}>
          {status.kind === 'in-game' && status.gameSlug ? (
            <>
              In game: <Link to={`/g/${status.gameSlug}`}>{status.gameTitle}</Link>
            </>
          ) : (
            label
          )}
        </span>
      </span>
      {onGreet && (
        <button
          className="btn btn--ghost btn--sm presence__greet"
          onClick={() => onGreet(row.handle)}
          title={`Say hi to @${row.handle}`}
          aria-label={`Say hi to @${row.handle} in chat`}
          data-testid={`presence-greet-${row.handle}`}
        >
          👋
        </button>
      )}
    </li>
  );
}

export function PresenceRail() {
  const services = useServices();
  const dock = useChatDock();
  const canChat = useCanChat();
  const rows = useSubscription<Array<UserPresence & { id: string }>>(
    services.subscribe.presence,
    [],
    [],
  );
  const now = useNow(TICK_MS);
  const entries = useMemo(() => visiblePresence(rows, now), [rows, now]);
  // The rail's second way IN: a wave opens the Clubhouse with that person
  // already addressed — the whole of what we shipped instead of DMs.
  //
  // Gated on {@link useCanChat}, NOT on "is the dock's provider mounted": the
  // provider is always mounted at the App root, so gating on it rendered a wave
  // for logged-out visitors that opened a dock which then refused to render.
  // Undefined here means the button never renders at all.
  const greet = useMemo(
    () => (dock && canChat ? (handle: string) => dock.openChat(`@${handle} `) : undefined),
    [dock, canChat],
  );
  const liveCount = entries.filter(
    (e) => e.status.kind === 'in-game' || e.status.kind === 'online',
  ).length;

  return (
    <section className="presence" data-testid="presence-rail">
      <div className="presence__header">
        <Mascot context="presence" quip={false} size={64} />
        <div>
          <h2 className="presence__title">Who's around</h2>
          <p className="presence__subtitle">
            {liveCount > 0
              ? `${liveCount} ${liveCount === 1 ? 'player' : 'players'} online right now`
              : 'Nobody online right now — recent players below'}
          </p>
        </div>
      </div>
      {entries.length === 0 ? (
        <p className="muted" data-testid="presence-empty">
          Quiet out here. Play something and you'll be the first one on the board.
        </p>
      ) : (
        <ul className="presence__list" data-testid="presence-list">
          {entries.map((entry) => (
            <PresenceRow key={entry.row.id} entry={entry} onGreet={greet} />
          ))}
        </ul>
      )}
    </section>
  );
}
